import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { formatFromLocalDate } from 'app/shared/util/date-utils';
import DateTextInput from 'app/component/date-text-input';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  addContractPaymentOutsideImportedPayment,
  deleteContractPayment,
  getContractPaymentsById,
  updateContractPayment,
} from 'app/api/contractApi';
import { Divider } from 'primereact/divider';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { SortOrder } from 'primereact/api'; // Define Yup validation schema

// Define Yup validation schema
const validationSchema = Yup.object({
  contract: Yup.object().required('Toto je povinný údaj'),
  variableSymbol: Yup.string().max(10, 'Max 10 znakov').required('Toto je povinný údaj'),
});

const CreateContractPayments = ({ id, readOnly }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [contractPayments, setContractPayments] = useState([]);
  const [showContractPaymentDialog, setShowContractPaymentDialog] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);
  const [changedContractId, setChangedContractId] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);
  const isAdminOrAssistant = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.ASSISTANT]),
  );

  useEffect(() => {
    if (id) {
      getContractPaymentsById(id)
        .then(response => {
          setData(response.data);
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const addOrUpdatePaymentContract = () => {
    setLoading(true);

    let call: Promise<any>;

    if (editContract.id) {
      call = updateContractPayment(editContract.id, editContract);
    } else {
      call = addContractPaymentOutsideImportedPayment(id, editContract);
    }

    call
      .then(response => {
        setData(response.data);
        setShowContractPaymentDialog(false);
        showSuccess('Úspech', 'Data boli úspešne uložené.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const onDeleteContractPayment = () => {
    setLoading(true);

    deleteContractPayment(editContract.id)
      .then(response => {
        setData(response.data);
        setDeletePaymentDialog(false);
        showSuccess('Úspech', 'Platba úspešne vymazaná.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const openDialog = contractPayment => {
    if (contractPayment.contractPayments) {
      contractPayment = contractPayment.contractPayments[0];
    }
    setEditContract(contractPayment);
    setShowContractPaymentDialog(true);
  };

  const confirmDeleteContact = contractToDelete => {
    setEditContract(contractToDelete);
    setDeletePaymentDialog(true);
  };

  const actionBodyTemplate = rowData => {
    return rowData?.contractPayments?.length < 2 ? (
      <React.Fragment>
        <div className="flex justify-content-around">
          {
            <Button
              icon="pi pi-pencil"
              type="button"
              id="contractPayment-edit"
              rounded
              outlined
              className="mr-2"
              onClick={() => openDialog(rowData)}
            />
          }
          {<Button icon="pi pi-trash" type="button" rounded outlined severity="danger" onClick={() => confirmDeleteContact(rowData)} />}
        </div>
      </React.Fragment>
    ) : (
      <div />
    );
  };

  const renderHeader = () => {
    return (
      <>
        {' '}
        {!readOnly && (
          <div className="flex justify-content-between">
            <Button
              type="button"
              icon="pi pi-plus"
              label="Pridať platbu"
              style={{ maxWidth: '150px' }}
              className="p-button-success"
              onClick={() => openDialog({})}
            />
            <div />
          </div>
        )}{' '}
      </>
    );
  };

  const header = renderHeader();

  const zaplateneNacasCheckbox = zaplatene => {
    return <Checkbox disabled={true} style={{ marginLeft: '1rem' }} checked={zaplatene as boolean} />;
  };

  const dialogFooter = (
    <div>
      <Button
        style={{ backgroundColor: 'gray' }}
        className="p-button-secondary"
        label="Zavrieť"
        icon="pi pi-times"
        onClick={() => setShowContractPaymentDialog(false)}
      />
      <Button
        style={{ backgroundColor: '#e31818' }}
        className="p-button-primary"
        label="Uložiť"
        onClick={() => addOrUpdatePaymentContract()}
      />
    </div>
  );

  const expandTemplate = rowData => {
    return (
      <div className="p-ml-4">
        <h5 style={{ color: 'blue' }}>Spracovanie platby</h5>
        <DataTable stripedRows value={rowData.contractPayments} responsiveLayout="scroll" size="small">
          <Column field={'periodFrom'} header="Od" body={v => formatFromLocalDate(v.periodFrom)} />
          <Column field={'periodTo'} header="Do" body={v => formatFromLocalDate(v.periodTo)} />
          <Column field={'zaplateneNacas'} header="Zaplatené načas" body={v => zaplateneNacasCheckbox(v.zaplateneNacas)} />
          <Column field={'zhodnotenieZacinaOd'} header="Zhodnotenie od" body={v => formatFromLocalDate(v.zhodnotenieZacinaOd)} />
          <Column field={'payedOn'} header="Zaplatené dňa" body={v => formatFromLocalDate(v.payedOn)} />
          <Column field={'amount'} header="Suma" body={v => v.amount + ' EUR'} />
          <Column field={'fee'} header="Poplatky" body={v => (v.fee ? v.fee : '0') + ' EUR'} />
          {!readOnly && <Column header="Akcie" body={actionBodyTemplate}></Column>}
        </DataTable>
        <Divider />
      </div>
    );
  };

  const deleteProductDialogFooter = (
    <React.Fragment>
      <Button label="Nie" icon="pi pi-times" outlined onClick={() => setDeletePaymentDialog(false)} />
      <Button label="Áno" icon="pi pi-check" severity="danger" onClick={onDeleteContractPayment} />
    </React.Fragment>
  );

  const allowExpansion = rowData => {
    return rowData.contractPayments.length > 1;
  };

  return (
    <>
      <div className="card" style={{ width: '100%' }}>
        <div>
          <DataTable
            stripedRows
            value={data}
            size="small"
            responsiveLayout="scroll"
            dataKey="id"
            sortField="periodFrom"
            sortOrder={SortOrder.DESC}
            emptyMessage="Žiadne platby."
            header={header}
            paginator
            rows={20}
            rowsPerPageOptions={[5, 10, 20, 50]}
            totalRecords={data?.length}
            rowExpansionTemplate={expandTemplate}
            expandedRows={expandedRows}
            onRowToggle={e => setExpandedRows(e.data)}
            filterDisplay="menu"
            loading={loading}
            className="table-row-with-line"
          >
            <Column expander={allowExpansion} style={{ width: '5rem' }} />
            <Column field={'periodFrom'} sortable header="Od" body={v => formatFromLocalDate(v.periodFrom)} />
            <Column field={'periodTo'} sortable header="Do" body={v => formatFromLocalDate(v.periodTo)} />
            <Column field={'zaplateneNacas'} sortable header="Zaplatené načas" body={v => zaplateneNacasCheckbox(v.zaplateneNacas)} />
            <Column field={'zhodnotenieZacinaOd'} sortable header="Zhodnotenie od" body={v => formatFromLocalDate(v.zhodnotenieZacinaOd)} />
            <Column field={'payedOn'} sortable header="Zaplatené dňa" body={v => formatFromLocalDate(v.payedOn)} />
            <Column field={'amount'} sortable header="Suma" body={v => v.amount + ' EUR'} />
            <Column field={'fee'} sortable header="Poplatky" body={v => (v.fee ? v.fee : '0') + ' EUR'} />
            <Column field={'requiredAmount'} header="Požadovaná suma" body={v => (v.requiredAmount ? v.requiredAmount : '0') + ' EUR'} />
            <Column header="Akcie" body={actionBodyTemplate}></Column>
          </DataTable>
        </div>
        {editContract && (
          <Dialog
            visible={showContractPaymentDialog}
            header="Spracovaná platba"
            style={{ width: '80vw' }}
            footer={dialogFooter}
            onHide={() => setShowContractPaymentDialog(false)}
          >
            {/* InputText for user query */}
            <div className="p-fluid grid">
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="zaplateneNacas">Zaplatené načas a v správnej výške:</label>
                  <Checkbox
                    style={{ marginLeft: '1rem' }}
                    checked={editContract.zaplateneNacas}
                    onChange={e => setEditContract({ ...editContract, zaplateneNacas: e.checked })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="poplatokVopred">Poplatok vopred:</label>
                  <Checkbox
                    style={{ marginLeft: '1rem' }}
                    checked={editContract.poplatokVopred}
                    onChange={e => setEditContract({ ...editContract, poplatokVopred: e.checked })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="mimoriadnyVklad">Mimoriadny vklad:</label>
                  <Checkbox
                    style={{ marginLeft: '1rem' }}
                    checked={editContract.mimoriadnyVklad}
                    onChange={e => setEditContract({ ...editContract, mimoriadnyVklad: e.checked })}
                  />
                </div>
              </div>

              <div className="col-6">
                <div className="p-field">
                  <label htmlFor="payedOn">Zaplatené dňa:</label>
                  <DateTextInput
                    id="payedOn"
                    value={editContract.payedOn}
                    onChange={val => setEditContract({ ...editContract, payedOn: val })}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="p-field">
                  <label htmlFor="zhodnotenieZacinaOd">Zhodnotenie začína od:</label>
                  <DateTextInput
                    id="zhodnotenieZacinaOd"
                    value={editContract.zhodnotenieZacinaOd}
                    onChange={val => setEditContract({ ...editContract, zhodnotenieZacinaOd: val })}
                  />
                </div>
              </div>

              <div className="col-6">
                <div className="p-field">
                  <label htmlFor="periodFrom">Platba platí pre dobu od:</label>
                  <DateTextInput
                    id="periodFrom"
                    value={editContract.periodFrom}
                    onChange={val => setEditContract({ ...editContract, periodFrom: val })}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="p-field">
                  <label htmlFor="periodTo">Platba platí pre dobu do:</label>
                  <DateTextInput
                    id="periodTo"
                    value={editContract.periodTo}
                    onChange={val => setEditContract({ ...editContract, periodTo: val })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="requiredAmount">Požadovaná výška platby:</label>
                  <InputNumber
                    id="requiredAmount"
                    value={editContract.requiredAmount}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={e => setEditContract({ ...editContract, requiredAmount: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="amount">Prijatá suma (bez poplatku):</label>
                  <InputNumber
                    id="amount"
                    value={editContract.amount}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={e => setEditContract({ ...editContract, amount: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="fee">Poplatok:</label>
                  <InputNumber
                    id="fee"
                    value={editContract.fee}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={e => setEditContract({ ...editContract, fee: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/*<div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>*/}
            {/*  <Button label="Vytvoriť novú osobu" type="button" onClick={() => onOpenCreateModal(null)} size="large" />*/}
            {/*</div>*/}
          </Dialog>
        )}
        <Dialog
          visible={deletePaymentDialog}
          style={{ width: '32rem' }}
          breakpoints={{ '960px': '75vw', '641px': '90vw' }}
          header="Potvrdenie"
          modal
          footer={deleteProductDialogFooter}
          onHide={() => setDeletePaymentDialog(false)}
        >
          <div className="confirmation-content">
            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
            {editContract?.periodFrom && (
              <span>
                Naozaj chcete vymazať priradenú platbu od: <b>{formatFromLocalDate(editContract?.periodFrom)}</b>?
              </span>
            )}
          </div>
        </Dialog>
      </div>
    </>
  );
};

export default CreateContractPayments;
